import { useFormStore } from "@/core/form/store/FormStore"
import CreateMemberGroupButton from "@/product/common/member-group/common/create/button/CreateMemberGroupButton"
import MemberGroupsMultiSelect from "@/product/common/member-group/modal/components/MemberGroupsMultiSelect"
import { ManageProductMemberGroupsButtonPreloadedQuery } from "@/product/member/admin/list/card/more-actions/manage-groups/ManageProductMemberGroupsButton"
import { ManageProductMemberGroupsButtonModalPreloadedQuery } from "@/product/member/admin/list/card/more-actions/manage-groups/__generated__/ManageProductMemberGroupsButtonModalPreloadedQuery.graphql"
import {
  EditGroupMembershipsInput,
  ManageProductMemberGroupsModalMutation,
} from "@/product/member/admin/list/card/more-actions/manage-groups/__generated__/ManageProductMemberGroupsModalMutation.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import ProfileAvatar from "@/user/common/avatar/ProfileAvatar"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  displayGraphQLErrorToast,
  displaySuccessToast,
} from "@components/toast/ToastProvider"
import { DiscoButton, DiscoModal, DiscoText, DiscoTextButton } from "@disco-ui"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import { useEffect, useState } from "react"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { graphql } from "relay-runtime"

interface ManageProductMemberGroupModalProps extends TestIDProps {
  isOpen: boolean
  onClose: VoidFunction
  productMembershipQueryRef: PreloadedQuery<ManageProductMemberGroupsButtonModalPreloadedQuery>
  refetchMemberships: () => void
}

function ManageProductMemberGroupsModal(props: ManageProductMemberGroupModalProps) {
  const {
    isOpen,
    onClose,
    productMembershipQueryRef,
    refetchMemberships,
    testid = "ManageProductMemberGroupsModal",
  } = props
  const classes = useStyles()

  const { node } = usePreloadedQuery<ManageProductMemberGroupsButtonModalPreloadedQuery>(
    ManageProductMemberGroupsButtonPreloadedQuery,
    productMembershipQueryRef
  )

  const [selectedGroupIds, setSelectedGroupIds] = useState<GlobalID[]>([])
  const productMembership = Relay.narrowNodeType(node, "ProductMembership")

  const form = useFormStore<
    ManageProductMemberGroupsModalMutation,
    Omit<EditGroupMembershipsInput, "addToGroups" | "removeFromGroups">
  >(
    graphql`
      mutation ManageProductMemberGroupsModalMutation(
        $input: EditGroupMembershipsInput!
      ) {
        response: editGroupMemberships(input: $input) {
          node {
            id
            ...useMemberGroupTagsList_ProductMembershipFragment
          }
          errors {
            field
            message
          }
        }
      }
    `,
    { productMembershipId: productMembership?.id || "" }
  )

  const memberGroupMemberships = Relay.connectionToArray(
    productMembership?.memberGroupMemberships
  )
  const initialGroups = memberGroupMemberships.map((mgm) => mgm.memberGroup)

  useEffect(() => {
    // Set the initial selected groups
    setSelectedGroupIds(initialGroups.map((mg) => mg.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGroups.length])

  if (!productMembership) return null

  return (
    <DiscoModal
      testid={testid}
      modalContentLabel={"Manage Groups"}
      isOpen={isOpen}
      onClose={onClose}
      title={"Manage Groups"}
      subtitle={"Add and remove group tags to manage the groups for:"}
      body={
        <>
          <div className={classes.selectedMembershipListItem}>
            <ProfileAvatar
              testid={`${testid}.UserAvatar`}
              className={classes.avatar}
              placeholderClassName={classes.avatar}
              userKey={productMembership.member}
              size={24}
            />
            <DiscoText color={"text.secondary"}>
              {productMembership.member.fullName}
            </DiscoText>
          </div>

          <MemberGroupsMultiSelect
            productKey={productMembership.product}
            selectedGroupIds={selectedGroupIds}
            setSelectedGroupIds={setSelectedGroupIds}
            allowGroupSelection={["custom"]}
          />
        </>
      }
      fullWidthButtons
      buttons={
        <>
          {/* Add Group Button */}
          <CreateMemberGroupButton
            groupKey={productMembership.product.defaultMemberGroup}
            isCreatingSubGroup
            selectedMembershipIds={[productMembership.id]}
            onCreate={onClose}
          >
            {({ onClick }) => (
              <DiscoTextButton
                data-testid={`${testid}.create-group-button`}
                leftIcon={"add"}
                onClick={onClick}
                className={classes.newGroup}
              >
                {"New Group"}
              </DiscoTextButton>
            )}
          </CreateMemberGroupButton>

          {/* Cancel Button */}
          <DiscoButton color={"grey"} variant={"outlined"} onClick={onClose}>
            {"Cancel"}
          </DiscoButton>

          {/* Save ChangesButton */}
          <DiscoButton
            onClick={handleSubmit}
            disabled={form.isSubmitting}
            data-testid={"MemberGroupFormFields.submit"}
          >
            {"Save Changes"}
          </DiscoButton>
        </>
      }
    />
  )

  async function handleSubmit() {
    try {
      const { added: addToGroups, removed: removeFromGroups } = ArrayUtils.diff(
        initialGroups.map((mg) => mg.id),
        selectedGroupIds
      )
      const { didSave } = await form.submit({
        productMembershipId: productMembership!.id,
        addToGroups,
        removeFromGroups,
      })

      if (didSave) {
        await refetchMemberships()
        displaySuccessToast({
          message: "Groups updated!",
          testid: "MemberGroupForm.success-toast",
        })

        onClose()
      }
    } catch (error) {
      displayGraphQLErrorToast(error)
    }
  }
}

const useStyles = makeUseStyles((theme) => ({
  selectedMembershipListItem: {
    display: "flex",
    borderRadius: theme.measure.borderRadius.big,
    boxShadow: theme.palette.groovyDepths.insideCard,
    padding: theme.spacing(1.5),
    margin: theme.spacing(1.25, 0.5, 2.5, 0.5),
  },
  avatar: {
    marginRight: theme.spacing(1.5),
  },
  newGroup: {
    marginRight: "auto",
  },
}))

export default ManageProductMemberGroupsModal

// eslint-disable-next-line no-unused-expressions
graphql`
  fragment ManageProductMemberGroupsModalFragment on ProductMembership {
    id
    member {
      id
      fullName
      ...ProfileAvatarFragment
    }
    memberGroupMemberships(groupKinds: [custom]) {
      edges {
        node {
          id
          memberGroup {
            id
          }
        }
      }
    }
    product {
      defaultMemberGroup {
        ...CreateMemberGroupButtonFragment
      }
      ...MemberGroupsMultiSelect_ProductFragment
    }
  }
`
